import {
    LinkableWallet,
    LinkWalletURIValue,
    CheckBalanceApiResponse,
    AddMoneyToWalletParams,
    AddMoneyWalletCode,
} from "@payment-client/interfaces/WalletPublic";
import { isString, isNil, isObject, isNumber } from "@swiggy-private/js-utils";
import { LINK_WALLET_URI } from "@payment-client/constants/WalletV3Endpoints";
import { Result, Err, Ok, Option, None, Some } from "@swiggy-private/js-fp";
import { STATUS_CODE_TO_LINK_STATUS } from "@payment-client/constants/WalletV3Endpoints";
import * as WalletApi from "@payment-client/interfaces/WalletApi";
import { WALLET_URLS } from "@payment-client/constants/WalletUrls";

// interfaces
import { Response } from "@swiggy-private/checkout-core";

// helpers
import { VALID_WALLET_LINK_STATUSES } from "@swiggy-private/payment-core";

interface WalletHttpOptionsResponse {
    method: "GET" | "POST" | "DELETE";
    endpoint: string;
}

export function isLinkableWallet(walletCode: string): walletCode is LinkableWallet {
    return Object.values(LinkableWallet).indexOf(walletCode as LinkableWallet) !== -1;
}

export function getWalletHttpOptions(
    walletCode: string,
    key: keyof LinkWalletURIValue,
    method: "GET" | "POST" | "DELETE" = "GET",
): Result<WalletHttpOptionsResponse, string> {
    if (!isLinkableWallet(walletCode)) {
        return Err(`${walletCode} is not a linkable wallet`);
    }

    const wallet = LINK_WALLET_URI[walletCode];
    const value = wallet[key];

    if (isNil(value)) {
        return Err(`${walletCode} does not support ${key} action.`);
    }

    if (isString(value)) {
        return Ok({ endpoint: value, method });
    }

    return Ok(value);
}

export function getWalletLinkStatus(
    walletCode: string,
    statusCode: number | string,
): Option<VALID_WALLET_LINK_STATUSES> {
    if (!isLinkableWallet(walletCode) || !STATUS_CODE_TO_LINK_STATUS[walletCode] || isNil(statusCode)) {
        return None;
    }

    const statusMap = STATUS_CODE_TO_LINK_STATUS[walletCode];
    const code = isString(statusCode) ? parseInt(statusCode, 10) : statusCode;
    const status = statusMap[code];

    return isNil(status) ? None : Some(status);
}

export function getWalletBalance(walletCode: string, data?: CheckBalanceApiResponse | null): Option<string | number> {
    if (isNil(data) || !isObject(data)) {
        return None;
    }

    switch (walletCode) {
        case LinkableWallet.PAYTM:
            return Some(data.walletBalance);
        case LinkableWallet.MOBIKWIK:
            return Some(data.balanceamount);
        case LinkableWallet.PHONEPE: {
            const val: number = Some(data.wallet)
                .map((wallet) => wallet?.usableBalance)
                .unwrapOr(0);
            return Some(isNumber(val) ? Number(val / 100).toFixed(2) : 0);
        }
        case LinkableWallet.AMAZONPAY:
            return Some(data.totalBalance);
        default:
            return None;
    }
}

function getWalletLinkedMobileNumber(walletCode: string, data?: CheckBalanceApiResponse | null): Option<string> {
    if (isNil(data) || !isObject(data)) {
        return None;
    }

    switch (walletCode) {
        case LinkableWallet.PAYTM:
        case LinkableWallet.MOBIKWIK:
            return Some(data.mobileNumber);
        case LinkableWallet.PHONEPE:
            return Some(data.wallet?.mobileNumber);
        default:
            return None;
    }
}

function checkIsOldWalletLinkFlow(walletCode: string, data?: CheckBalanceApiResponse | null): Option<boolean> {
    if (isNil(data) || !isObject(data)) {
        return None;
    }

    switch (walletCode) {
        case LinkableWallet.PAYTM:
        case LinkableWallet.MOBIKWIK:
            return Some(data.isOld);
        case LinkableWallet.PHONEPE:
            return Some(data.wallet?.isOld);
        default:
            return None;
    }
}

export function transformCheckBalanceApiResponse(
    walletCode: string,
    response: Response<CheckBalanceApiResponse>,
): WalletApi.GetWalletBalanceResponse {
    const linkStatus = Some(response.statusCode)
        .andThen((code) => getWalletLinkStatus(walletCode, code))
        .unwrapOr(VALID_WALLET_LINK_STATUSES.RETRY);
    const balance = getWalletBalance(walletCode, response.data).unwrapOr(0);
    const walletLinkedMobileNumber = getWalletLinkedMobileNumber(walletCode, response.data).unwrapOr("");
    const isOldWalletLinkFlow = checkIsOldWalletLinkFlow(walletCode, response.data).unwrapOr(false);

    return {
        linkStatus,
        balance,
        mobileNumber: walletLinkedMobileNumber,
        isOldWalletLinkFlow,
    };
}

export function getWalletAddMoneyReturnUrl(walletCode: AddMoneyWalletCode, params: AddMoneyToWalletParams): string {
    if (params.addMoneyReturnUrl) {
        return params.addMoneyReturnUrl;
    }

    if (params.isPaymentModeFlow) {
        return `${WALLET_URLS.PAYMENT_MODE_ADD_MONEY_RETURN_URL}?walletCode=${params.walletCode}`;
    }

    switch (walletCode) {
        case LinkableWallet.PAYTM:
            return WALLET_URLS.PAYTM_CALLBACK_URL || "";
        case LinkableWallet.MOBIKWIK:
            return WALLET_URLS.RETURN_URL || "";
        default:
            return "";
    }
}

const SUCCESS_STATUS_CODE = 0;
const ERROR_STATUS_CODE = 1;

export const successResponse = <T>(data?: T | null, statusMessage?: string | null): Response<T> => {
    return {
        statusMessage,
        data,
        statusCode: SUCCESS_STATUS_CODE,
    };
};

export const errorResponse = <T>(statusMessage?: string | null): Response<T> => {
    return {
        statusMessage,
        statusCode: ERROR_STATUS_CODE,
    };
};

export const getWalletKey = (walletCode: string): Option<{ key: keyof LinkWalletURIValue; method: "GET" | "POST" }> => {
    switch (walletCode) {
        case LinkableWallet.PAYTM:
            return Some({ key: "ssoToken", method: "GET" });
        case LinkableWallet.MOBIKWIK:
            return Some({ key: "calculateChecksum", method: "POST" });
    }
    return None;
};
